import { Alignment, Api, ApiMetadata, ApiMetadataRetriever } from "@mcleod/core";
import {
    Button, ChangeEvent, ForcedCase, Label, Layout, LayoutProps, PanelStaticError, SlideoutDecorator, Textbox
} from "@mcleod/components";
import { UserUtil } from "./UserUtil";
import { UserLayoutProvider } from "../UserLayoutProvider";

export class CopyUser extends Layout {
    private buttonCreateNewUser: Button;
    private endpointPath: string;
    private slideoutDecorator: SlideoutDecorator;
    private textboxNewUserId: Textbox;
    private textboxNewUserName: Textbox;
    private sourceUserId: string;
    private sourceUserName: string;

    constructor(endpointPath: string, sourceUserId: string, sourceUserName: string, props?: Partial<LayoutProps>) {
        super({
            ...props,
            id: "layoutCopyUser",
            needsServerLayout: false,
            padding: 12
        });
        this.endpointPath = endpointPath;
        this.sourceUserId = sourceUserId;
        this.sourceUserName = sourceUserName;
    }

    public async fillAndShow() {
        // Only allow the copy user action for users that have permission to use the Users screen
        if (!UserUtil.userHasUserListAccess()) {
            const panelError = new PanelStaticError("Access denied due to permissions");
            this.add(panelError);
            return;
        }
        const labelInstructionsText = `To copy user ${this.sourceUserName} (${this.sourceUserId}), enter the new ` +
            "user's ID and name below, and click 'Create New User'.  After it has been saved, the new user record will be " +
            "displayed in a new tab to allow any additional changes to be made.";
        const labelInstructions = new Label({
            id: "labelInstructions",
            marginBottom: 20,
            text: labelInstructionsText
        });
        // Assume metadata is loaded, since user came from a user screen to get here
        const metadata = await ApiMetadataRetriever.get("common/user/user-settings");
        this.createNewUserIdTextbox(metadata);
        this.createNewUserNameTextbox(metadata);
        this.add(labelInstructions, this.textboxNewUserName, this.textboxNewUserId);
        this.show();
    }

    private createNewUserIdTextbox(metadata: ApiMetadata) {
        this.textboxNewUserId = new Textbox({
            caption: "New User ID",
            forcedCase: ForcedCase.LOWER,
            id: "textboxNewUserId",
            required: true
        });
        const maxLength = metadata.output?.id?.length ?? 10;
        this.textboxNewUserId.addChangeListener((event: ChangeEvent) => {
            this.checkMaxLength(this.textboxNewUserId, event, maxLength);
        });
    }

    private createNewUserNameTextbox(metadata: ApiMetadata) {
        this.textboxNewUserName = new Textbox({
            caption: "New User Name",
            id: "textboxNewUserName",
            required: true
        });
        const maxLength = metadata.output?.name?.length ?? 40;
        this.textboxNewUserName.addChangeListener((event: ChangeEvent) => {
            this.checkMaxLength(this.textboxNewUserName, event, maxLength);
        });
    }

    private checkMaxLength(textbox: Textbox, event: ChangeEvent, length: number) {
        if ((textbox.text ?? "").length > length) {
            const content = `You have exceeded the ${length} character limit.`;
            const options = { position: Alignment.RIGHT, shaking: true, timeout: 5000 };
            textbox.showAdhocTooltip(content, options);
            textbox.text = event.oldValue;
        }
    }

    private createCopyUserButton(): Button {
        this.buttonCreateNewUser = new Button({
            caption: "Create New User",
            default: true,
            id: "buttonCopyUser",
            onClick: () => this.createNewUser(),
            rowBreak: false
        });
        return this.buttonCreateNewUser;
    }

    private show() {
        this.slideoutDecorator = new SlideoutDecorator({
            // Do not display Save button if the user doesn't have permissions to change user passwords.  In that case,
            // a static error will be displayed instead of the two password textboxes (which were not created).
            addlComponents: this.textboxNewUserId == null ? [] : [ this.createCopyUserButton() ],
            fillVerticalSpace: true,
            layout: this,
            title: "Copy User",
            width: 550
        });
    }

    private async createNewUser() {
        if (this.validateSimple()) {
            try {
                this.buttonCreateNewUser.busy = true;
                const response = await Api.post(this.endpointPath, {
                    source_user_id: this.sourceUserId,
                    new_user_id: this.textboxNewUserId.text,
                    new_user_name: this.textboxNewUserName.text
                });
                this.slideoutDecorator.slideOut();
                UserLayoutProvider.navigateTo(this.textboxNewUserId.text, { newTab: true });
            } finally {
                this.buttonCreateNewUser.busy = false;
            }
        }
    }
}
